/*
 * The windowed list: the timeline a stream of items is read in — a chat conversation, a thread, an AI
 * chat's transcript. A hard DOM cap instead of virtualisation (A30, spec 12 §4.3): at most
 * `DOM_CAP` rows exist at a time, the window pages in both directions, and every row on screen is
 * real text a browser can search — which is exactly what a virtualised list takes away.
 * react-virtuoso is pinned and unused; it becomes the default only if the frame budget says so.
 * (`Ctrl+F` in a chat conversation is its own search — it looks through the whole history rather
 * than the three hundred rows in the DOM, which is the trade Slack makes as well.)
 *
 * Four behaviours are mechanisms rather than inventions:
 *   - sticky bottom is decided *inside* onScroll and only read afterwards (REUSE J6): measuring
 *     after the list has grown always answers "not at the bottom" and the view drifts up by one
 *     message per arrival;
 *   - a ResizeObserver on the row container *and on the scroller itself* re-applies it, because
 *     markdown, late images, a composer growing under the list and fonts settling all change the
 *     geometry without any scroll event (X-A2 D2, D3) — and a change of the scroller's *width*
 *     re-applies it inside `measure`, because a narrower column rewraps every paragraph: what looks
 *     like the reader leaving the bottom is the text growing under them (D-10 D7 — opening the
 *     details column used to push the last message under the composer);
 *   - paging into the past is asked for by a one-pixel sentinel above the first row, watched by an
 *     IntersectionObserver with 200 px of margin and re-armed after every commit, so a reader who
 *     wheels to the top and stays there is still served the next page (X-A1 D1);
 *   - every commit that is not a stick restores the first rendered row to the pixel it had before
 *     the commit, measured against the row and never against `scrollHeight` — so a prepend that
 *     also trims the tail of the DOM window, or a loading row appearing above, moves nothing under
 *     the reader; and because the rendered slice is a range of indices into an array that the
 *     memory ceiling shortens at its front, the window is put back on the row it was showing before
 *     the anchor is restored (`headMovedTo`).
 *
 * An arriving item never moves focus and never scrolls unless the reader was already at the
 * bottom ([25] B3); the jump pill is what tells them there is something below, and its count is
 * what they have not had on screen yet — not the whole unread span (X-A1 D16) — and it is printed
 * only while the stream's own unread count backs it up: after a jump to a date, the rows below the
 * window are items the reader has *read*, and "93 new messages" was the pill lying about them
 * (D-10 D6). The words are the surface's (`newLabel`): the list counts, the surface names.
 *
 * The list knows nothing of what a row is. The surface describes each row through `shapeOf` — its
 * key, its kind, the seq of the item it draws — and everything below works on those shapes, so the
 * rules here hold for every stream that is read top to bottom (types.ts).
 *
 * The arrow keys and `j`/`k` move a roving tabindex between rows, PageUp/PageDown by a screenful; the handler is
 * attached to the scroller element rather than as a JSX prop, because the interaction belongs to the
 * roving stops and not to the log region that contains them.
 *
 * The roving stop is published to the rows through `ActiveRowContext`, because it decides more than
 * which `<li>` is tabbable: a row's own controls are in the tab sequence only while its row is the
 * stop, which is what keeps the composer a few Tab presses below any row (activeRow.ts).
 */

import {
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { t } from "../i18n/index.ts"
import { Icon } from "../shell/index.ts"
import { ActiveRowContext, createActiveRowSource } from "./activeRow.ts"
import "./list.css"
import type { ListHandle, RowShape, WindowedListProps } from "./types.ts"

/** The hard cap: how many rows may be in the DOM at once. */
export const DOM_CAP = 300
/**
 * How far the DOM window moves when the reader wheels past its edge. A quarter of the cap and not a
 * half: replacing rows is the most expensive thing this list does — one commit, all of it — and the
 * step is what decides the size of that commit. Measured over ten shifts of a 1 050-message channel
 * on a production build (X-B3 D7's case): the longest task per shift falls from a median of 357 ms at
 * 150 rows to 142 ms at 75, and the worst from 499 ms to 163 ms, for the same total work over a long
 * scroll — twice as many shifts, each less than half the cost. Wrapping the same 150-row commit in
 * `startTransition` instead was measured at 245 ms, because what costs is the commit and a transition
 * only makes the render before it interruptible.
 *
 * The reader still has 75 rows of runway above them after an upward shift, which is far more than the
 * sentinel's 200 px of margin needs.
 */
export const SHIFT_ROWS = DOM_CAP / 4
/** How far from the bottom counts as "there", in pixels, for paging the window down. */
const EDGE_PX = 200
const STICKY_TOLERANCE_PX = 24
/** How far above the top of the loaded rows the next page is asked for. */
const SENTINEL_MARGIN = "200px 0px 0px 0px"
/**
 * How long a permalink target stays highlighted. It is `tb-row-flash`'s own length in `list.css` —
 * 1 200 ms of full colour and a 400 ms fade — so the class is taken off the row as the animation
 * ends rather than a frame before or a second after.
 */
const HIGHLIGHT_MS = 1600
/** The context above a row scrolled to `start` — enough for the divider's neighbours to be seen. */
const CONTEXT_PX = 96

/**
 * The class the <li> around one row carries. The kind is on the row itself and not only inside it,
 * because two rules need the element: the day pill sticks, and a sticky element is bounded by its
 * own containing block — inside the <li> it would never travel — and the hover tint is full bleed,
 * which is the <li>'s width and not the row's.
 */
function rowClass(shape: RowShape, highlighted: boolean): string {
	return `tb-row tb-row--${shape.kind}${highlighted ? " tb-row--highlight" : ""}`
}

/**
 * What every render of the list needs to know about the whole row array, in one walk of it.
 *
 * It is one walk and not several because the array is the *loaded* window and not the DOM window:
 * the empty state and the keyboard's walk each read every row the surface holds, on every render,
 * and a render happens on every arriving item. A surface caps that array (chat's `state/trim.ts` at
 * a thousand rows); this makes the cost of it one pass per new array.
 *
 * `seqs` is the roving walk's own array and is what the arrow keys index into.
 */
interface RowFacts {
	/**
	 * Whether the stream has anything in it. A loaded conversation with no messages is not an empty
	 * row list — chat still opens it with the history-start divider — so the empty state has to ask
	 * about items and sends, not about rows.
	 */
	hasItems: boolean
	/** The seq of the newest item row: where the roving stop starts. */
	lastItemSeq: number
	seqs: number[]
}

function factsOf(shapes: readonly RowShape[]): RowFacts {
	const seqs: number[] = []
	let hasPending = false
	for (const shape of shapes) {
		if (shape.seq > 0) seqs.push(shape.seq)
		else if (shape.pending === true) hasPending = true
	}
	return {
		hasItems: seqs.length > 0 || hasPending,
		lastItemSeq: seqs[seqs.length - 1] ?? 0,
		seqs,
	}
}

/**
 * Where the row the DOM window started at stands now, or -1 when it stands where it did — or when it
 * is gone, or above where it was.
 *
 * The window is a range of *indices* into `rows`, and that array loses rows off its front: past the
 * memory ceiling every arriving item drops the oldest row of the loaded window (chat's
 * `state/trim.ts`), so every surviving row moves down one place while `windowStart` stands still.
 * The rendered slice would then walk one row towards the tail for every message that arrives, under a
 * reader who has not moved, and the anchor that keeps them where they are would be looking for the
 * very row that has just fallen out of the slice.
 *
 * The search goes backwards only, and it is what makes this cheap: a drop moves the head down by the
 * one or two rows that went, so the first step usually finds it. Rows coming in *above* the window —
 * a page of older history — move it the other way, and there the anchor already answers: the row is
 * still rendered, further down, and the scroll is corrected by how much arrived above it.
 */
function headMovedTo(shapes: readonly RowShape[], start: number, head: string | null): number {
	if (head === null || start <= 0) return -1
	if (shapes[start]?.key === head) return -1
	for (let index = Math.min(start, shapes.length) - 1; index >= 0; index--) {
		if (shapes[index]?.key === head) return index
	}
	return -1
}

/** A text control the reader is typing in: its own keys win over the list's. */
function isEditable(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false
	const tag = target.tagName
	return tag === "TEXTAREA" || tag === "INPUT" || target.isContentEditable
}

/**
 * The seq of the row `<li>` a key was pressed *on*, or 0 when it was pressed on something inside
 * one. The distinction is the whole rule for `Enter`: on the row it means "open this one", inside
 * it — on a button of the hover bar, in the edit box — it belongs to that control.
 */
function seqOfRowElement(target: EventTarget | null): number {
	if (!(target instanceof HTMLElement)) return 0
	const seq = target.dataset["seq"]
	return seq === undefined ? 0 : Number(seq)
}

/** Where a row's top stands inside the scroller's own viewport. */
function rowTop(scroller: HTMLElement, row: HTMLElement): number {
	return row.getBoundingClientRect().top - scroller.getBoundingClientRect().top
}

/**
 * The seqs of the item rows on screen right now — the first whose bottom is below the top edge
 * and the last whose top is above the bottom edge. The rows are in order, so two binary searches
 * over the rendered `<li>`s answer it with a handful of rectangle reads instead of three hundred.
 */
function visibleSeqs(scroller: HTMLElement): { from: number; to: number } {
	const lis = scroller.querySelectorAll<HTMLElement>("li[data-seq]")
	if (lis.length === 0) return { from: 0, to: 0 }
	const box = scroller.getBoundingClientRect()
	let lo = 0
	let hi = lis.length - 1
	while (lo < hi) {
		const mid = (lo + hi) >> 1
		if (lis[mid]!.getBoundingClientRect().bottom <= box.top) lo = mid + 1
		else hi = mid
	}
	const first = lo
	hi = lis.length - 1
	while (lo < hi) {
		const mid = (lo + hi + 1) >> 1
		if (lis[mid]!.getBoundingClientRect().top >= box.bottom) hi = mid - 1
		else lo = mid
	}
	return { from: Number(lis[first]!.dataset["seq"]), to: Number(lis[lo]!.dataset["seq"]) }
}

/**
 * Which row an arrow, `j`, `k`, Home, End, PageUp or PageDown moves the roving focus to, or null when
 * the key is none of the list's. A page is a screenful: the farthest row in that direction whose
 * distance from the current one is still inside the scroller's height, and at least one row.
 *
 * `j` and `k` are `↓` and `↑`: the muscle memory half the world brings from every other timeline
 * (X-A4 N16). They are registered bindings as well, so the sheet documents them and pressing one
 * from outside the list puts the focus on the roving stop first — from there this is what answers.
 */
function targetIndex(
	key: string,
	current: number,
	seqs: readonly number[],
	scroller: HTMLElement,
	rowElement: (seq: number) => HTMLElement | null,
): number | null {
	switch (key) {
		case "ArrowUp":
		case "k":
			return Math.max(0, current - 1)
		case "ArrowDown":
		case "j":
			return Math.min(seqs.length - 1, current + 1)
		case "Home":
			return 0
		case "End":
			return seqs.length - 1
		case "PageUp":
		case "PageDown": {
			const step = key === "PageUp" ? -1 : 1
			const from = rowElement(seqs[current] ?? 0)
			if (!from) return null
			const origin = rowTop(scroller, from)
			let index = current
			for (let next = current + step; next >= 0 && next < seqs.length; next += step) {
				const el = rowElement(seqs[next] ?? 0)
				if (!el || Math.abs(rowTop(scroller, el) - origin) >= scroller.clientHeight) break
				index = next
			}
			return index === current ? Math.max(0, Math.min(seqs.length - 1, current + step)) : index
		}
		default:
			return null
	}
}

interface ScrollRequest {
	seq: number
	align: "start" | "center" | "end"
	highlight: boolean
	/** Leave the roving tabindex on the row once it is there — a jump a key asked for. */
	focus: boolean
}

export function WindowedList<R>({
	rows,
	shapeOf,
	renderRow,
	onReachTop,
	onReachBottom,
	onVisibleRangeChange,
	stickToBottom,
	handleRef,
	empty,
	onActivateRow,
	onEscape,
	onJumpToLatest,
	loadingOlder = false,
	loadingOlderLabel,
	newerCount = 0,
	unread = 0,
	newLabel,
}: WindowedListProps<R>) {
	const scrollerRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLOListElement>(null)
	const sentinelRef = useRef<HTMLDivElement>(null)
	const stickRef = useRef(stickToBottom)
	const [atBottom, setAtBottom] = useState(true)
	const [windowStart, setWindowStart] = useState(Math.max(0, rows.length - DOM_CAP))
	const [focusedSeq, setFocusedSeq] = useState(0)
	const [highlightSeq, setHighlightSeq] = useState(0)
	/** The newest seq that has been on screen; the pill counts what lies past it. */
	const [seenSeq, setSeenSeq] = useState(0)
	/** The first rendered row and where it stood, taken before a commit and restored after it. */
	const anchorRef = useRef<{ seq: number; top: number } | null>(null)
	/** The row the DOM window starts at, and the array and index it was read from (`headMovedTo`). */
	const headRef = useRef<string | null>(null)
	const seenRef = useRef<{ shapes: readonly RowShape[]; start: number } | null>(null)
	/** A scroll asked for while its row was not in the DOM window: served by the next commit. */
	const pendingScrollRef = useRef<ScrollRequest | null>(null)
	/** The scroller's width and height at the last measure; a change is a relayout, not a scroll. */
	const sizeRef = useRef([0, 0])
	const visibleRef = useRef({ from: 0, to: 0 })
	// The callbacks change identity on every render of the surface; the observers and the measure
	// read the latest through one ref rather than being rebuilt each time.
	const callbacks = useRef({ onReachTop, onReachBottom, onVisibleRangeChange })
	useLayoutEffect(() => {
		callbacks.current = { onReachTop, onReachBottom, onVisibleRangeChange }
	})

	// Every rule below reads the shapes, never the rows: one walk per new row array, which is also why
	// a surface passes a `shapeOf` that keeps its identity.
	const shapes = useMemo(() => rows.map(shapeOf), [rows, shapeOf])
	const start = Math.max(0, Math.min(windowStart, Math.max(0, rows.length - DOM_CAP)))
	const end = Math.min(rows.length, start + DOM_CAP)
	const facts = useMemo(() => factsOf(shapes), [shapes])
	// An empty stream shows its invitation instead of the divider that is all it otherwise has.
	const showEmpty = empty !== undefined && !facts.hasItems

	// Nothing focused yet means the newest item is the stop, which is where the arrow keys start
	// — so the rows are told the same seq the tabindex below uses.
	const activeSeq = focusedSeq === 0 ? facts.lastItemSeq : focusedSeq
	const activeRows = useMemo(() => createActiveRowSource(), [])
	useEffect(() => activeRows.set(activeSeq), [activeRows, activeSeq])

	// The window follows the end of the list while the reader is at the bottom; once they scroll
	// up it stays where it is until they come back or page further.
	useEffect(() => {
		if (stickRef.current) setWindowStart(Math.max(0, rows.length - DOM_CAP))
	}, [rows.length])

	// A surface that says it will land somewhere of its own — on a permalink, on the unread
	// divider — takes the stick away before the rows commit, so the list does not touch the bottom
	// first: what the first measure reports as seen, and the count the pill starts from, are then
	// what the landing shows. `true` releases nothing: whether to stick is the geometry's decision.
	useLayoutEffect(() => {
		if (!stickToBottom) stickRef.current = false
	}, [stickToBottom])

	/** An item row by its seq — or the row standing in for an item that is gone. */
	const rowElement = useCallback((seq: number): HTMLElement | null => {
		return (
			scrollerRef.current?.querySelector<HTMLElement>(
				`[data-seq="${seq}"], [data-gone="${seq}"]`,
			) ?? null
		)
	}, [])

	/**
	 * Reads the geometry once: whether the reader is at the bottom, which rows are on screen, and
	 * where the first rendered row stands — the anchor the next commit restores. Called after every
	 * scroll, every commit and every resize; nothing else measures.
	 */
	const measure = useCallback(() => {
		const el = scrollerRef.current
		// A pane dragged to zero width has no scroll position worth keeping.
		if (!el || el.offsetParent === null) return
		// A narrower column rewraps every paragraph, so the content grows below a reader who has not
		// moved — and the scroll event that the reflow produces would otherwise be read as "they left
		// the bottom". A shorter scroller does the same from below: a band or a run line drawn under
		// the list after its first rows takes its height off the viewport, and the bottom moves away
		// from a reader who did not scroll. While either changed, the bottom is re-applied instead of
		// measured away; this is the one place that needs to know, because the scroll event and the
		// ResizeObserver race and whichever arrives first sees the new size (D-10 D7).
		const [width, height] = sizeRef.current
		sizeRef.current = [el.clientWidth, el.clientHeight]
		// A scroller that had no height yet was never laid out, and its first one is no relayout.
		const resized = el.clientWidth !== width || (height !== 0 && el.clientHeight !== height)
		if (resized && stickRef.current) el.scrollTop = el.scrollHeight
		const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight
		const stuck = bottomGap < STICKY_TOLERANCE_PX
		stickRef.current = stuck
		setAtBottom(stuck)

		const range = visibleSeqs(el)
		if (range.from !== visibleRef.current.from || range.to !== visibleRef.current.to) {
			visibleRef.current = range
			if (range.to !== 0) callbacks.current.onVisibleRangeChange(range.from, range.to)
		}
		if (range.to !== 0) setSeenSeq((seen) => Math.max(seen, range.to))

		const first = el.querySelector<HTMLElement>("li[data-seq]")
		anchorRef.current = first ? { seq: Number(first.dataset["seq"]), top: rowTop(el, first) } : null
	}, [])

	const applyStick = useCallback(() => {
		const el = scrollerRef.current
		if (!el || el.offsetParent === null || !stickRef.current) return
		el.scrollTop = el.scrollHeight
	}, [])

	const performScroll = useCallback(
		(request: ScrollRequest) => {
			const el = scrollerRef.current
			const row = el ? rowElement(request.seq) : null
			if (!el || !row) return
			const top = rowTop(el, row) + el.scrollTop
			const target =
				request.align === "start"
					? top - CONTEXT_PX
					: request.align === "center"
						? top - (el.clientHeight - row.offsetHeight) / 2
						: top + row.offsetHeight - el.clientHeight
			el.scrollTop = Math.max(0, target)
			if (request.highlight) setHighlightSeq(request.seq)
			if (request.focus) {
				setFocusedSeq(request.seq)
				// The scroll above put the row where it should be; the focus must not move it again.
				row.focus({ preventScroll: true })
			}
			measure()
		},
		[rowElement, measure],
	)

	// After every commit, in this order: a scroll that was waiting for its row; else the stick; else
	// the window put back on the row it was showing when the rows moved under it; else the anchor —
	// the first rendered row back to the pixel it had, whatever was added above it or dropped below
	// it. Then the geometry is read again.
	useLayoutEffect(() => {
		const el = scrollerRef.current
		if (!el) return
		const pending = pendingScrollRef.current
		if (pending && rowElement(pending.seq)) {
			pendingScrollRef.current = null
			performScroll(pending)
		} else if (stickRef.current) {
			applyStick()
		} else {
			// The window follows the rows only when it is the rows that moved: a shift and a jump set
			// `windowStart` themselves, and both of those change `start`.
			const stood = seenRef.current
			const moved =
				stood !== null && stood.start === start ? headMovedTo(shapes, start, headRef.current) : -1
			if (moved >= 0) {
				// Nothing is measured against the slice that stood here for this one commit — the anchor
				// was taken on the rows that were on screen, and the commit this asks for puts them back.
				seenRef.current = { shapes, start }
				setWindowStart(moved)
				return
			}
			const anchor = anchorRef.current
			const row = anchor ? rowElement(anchor.seq) : null
			if (anchor && row) el.scrollTop += rowTop(el, row) - anchor.top
		}
		seenRef.current = { shapes, start }
		headRef.current = shapes[start]?.key ?? null
		measure()
	}, [shapes, start, loadingOlder, rowElement, performScroll, applyStick, measure])

	useEffect(() => {
		const el = scrollerRef.current
		const list = listRef.current
		if (!el || !list) return
		// The composer growing under the list shrinks the scroller: the stick has to follow that
		// too, not only the rows changing height.
		const observer = new ResizeObserver(() => {
			applyStick()
			measure()
		})
		observer.observe(list)
		observer.observe(el)
		return () => observer.disconnect()
	}, [applyStick, measure])

	/**
	 * Moves the DOM window by `SHIFT_ROWS`. It is the one place the window is moved *while the reader
	 * is scrolling*, as opposed to jumped (`scrollToSeq`, `scrollToBottom`), and it is what the size of
	 * the step is for.
	 */
	const shiftWindow = useCallback((next: number) => {
		setWindowStart(next)
	}, [])

	// The sentinel: while it stands inside the margin the next page is due — the DOM window's own
	// when it has one, the server's otherwise. Observed once; re-armed after every commit below,
	// because an observer only reports a change, and a reader parked at the top is not a change.
	const topReached = useRef(() => undefined as void)
	useLayoutEffect(() => {
		topReached.current = () => {
			if (start > 0) shiftWindow(Math.max(0, start - SHIFT_ROWS))
			else callbacks.current.onReachTop()
		}
	}, [start, shiftWindow])
	const sentinelObserver = useRef<IntersectionObserver | null>(null)
	useEffect(() => {
		const el = scrollerRef.current
		const sentinel = sentinelRef.current
		if (!el || !sentinel || typeof IntersectionObserver === "undefined") return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) topReached.current()
			},
			{ root: el, rootMargin: SENTINEL_MARGIN },
		)
		observer.observe(sentinel)
		sentinelObserver.current = observer
		return () => {
			observer.disconnect()
			sentinelObserver.current = null
		}
	}, [])
	useLayoutEffect(() => {
		const observer = sentinelObserver.current
		const sentinel = sentinelRef.current
		if (!observer || !sentinel) return
		observer.unobserve(sentinel)
		observer.observe(sentinel)
	}, [shapes, start, loadingOlder])

	useEffect(() => {
		if (highlightSeq === 0) return
		const timer = globalThis.setTimeout(() => setHighlightSeq(0), HIGHLIGHT_MS)
		return () => globalThis.clearTimeout(timer)
	}, [highlightSeq])

	const onScroll = useCallback(() => {
		const el = scrollerRef.current
		if (!el) return
		measure()
		const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight
		if (bottomGap < EDGE_PX) {
			if (end < rows.length) shiftWindow(Math.min(rows.length - DOM_CAP, start + SHIFT_ROWS))
			else callbacks.current.onReachBottom()
		}
	}, [measure, start, end, rows.length, shiftWindow])

	const scrollToSeq = useCallback(
		(
			seq: number,
			align: "start" | "center" | "end" = "center",
			highlight = false,
			focus = false,
		) => {
			// Only an item or its stand-in is a target: every other row carries seq 0.
			const index =
				seq > 0 ? shapes.findIndex((shape) => shape.seq === seq || shape.goneSeq === seq) : -1
			if (index < 0) return
			stickRef.current = false
			const request: ScrollRequest = { seq, align, highlight, focus }
			if (index < start || index >= end || !rowElement(seq)) {
				pendingScrollRef.current = request
				setWindowStart(Math.max(0, index - DOM_CAP / 2))
				return
			}
			performScroll(request)
		},
		[shapes, start, end, rowElement, performScroll],
	)

	/**
	 * The way home. The DOM window is re-anchored at the tail and the stick set *before* the scroll,
	 * so the commit that follows lands at the bottom too — and at the bottom of the tail once the
	 * surface has fetched it, when the loaded window was not the tail.
	 *
	 * It takes no behaviour: the list never animates its geometry (§1 rule 8), and the one caller that
	 * used to ask for `smooth` was scrolling past a screenful of rows, which is a blur and not a
	 * movement anybody follows (D-07 §1).
	 */
	const scrollToBottom = useCallback(() => {
		const el = scrollerRef.current
		stickRef.current = true
		setAtBottom(true)
		setWindowStart(Math.max(0, rows.length - DOM_CAP))
		el?.scrollTo({ top: el.scrollHeight })
	}, [rows.length])

	// Arrow keys walk the roving tabindex; Home and End jump to the ends of the loaded window,
	// PageUp and PageDown by a screenful.
	useEffect(() => {
		const el = scrollerRef.current
		if (!el) return
		function onKeyDown(event: KeyboardEvent) {
			// The listener sits on the scroller, so it sees every key pressed inside it — the edit
			// composer included, which is rendered inside a row. Typing in a text control is that
			// control's business: Home there goes to the start of the line, not to another message.
			if (isEditable(event.target) || event.defaultPrevented) return
			// Every key of the list is unmodified. A chord belongs to the application — `Alt+↓` is the
			// next conversation, `Shift+Escape` marks everything read — and a list that swallowed the
			// arrow out of one of them would take the binding away from the whole conversation.
			if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
			// Two keys belong to the row itself rather than to the roving walk. Both prevent their
			// default, which is also what keeps the registry's binding of the same key from running
			// a second time (UX §1.6).
			if (event.key === "Enter" || event.key === "Escape") {
				const seq = seqOfRowElement(event.target)
				if (seq === 0) return
				if (event.key === "Enter" && onActivateRow) {
					event.preventDefault()
					onActivateRow(seq)
				}
				if (event.key === "Escape" && onEscape) {
					event.preventDefault()
					onEscape()
				}
				return
			}
			const seqs = facts.seqs
			if (seqs.length === 0 || !el) return
			const anchor = focusedSeq === 0 ? (seqs[seqs.length - 1] ?? 0) : focusedSeq
			const current = Math.max(0, seqs.indexOf(anchor))
			const next = targetIndex(event.key, current, seqs, el, rowElement)
			if (next === null) return
			const seq = seqs[next]
			if (seq === undefined) return
			event.preventDefault()
			setFocusedSeq(seq)
			rowElement(seq)?.focus()
		}
		el.addEventListener("keydown", onKeyDown)
		return () => el.removeEventListener("keydown", onKeyDown)
	}, [facts, focusedSeq, rowElement, onActivateRow, onEscape])

	useImperativeHandle(
		handleRef,
		(): ListHandle => ({
			scrollToSeq,
			scrollToBottom,
			atBottom: () => stickRef.current,
			focusSeq(seq) {
				setFocusedSeq(seq)
				rowElement(seq)?.focus({ preventScroll: true })
			},
			visibleRange: () => visibleRef.current,
		}),
		[scrollToSeq, scrollToBottom, rowElement],
	)

	// What the pill says. The count is every item row past the newest seq that has been on screen
	// and written by somebody else, plus what the server holds below the window (X-A1 A9, D16).
	//
	// It is printed only as far as the stream's own unread count reaches. Everything below the fold
	// is unseen *in this window*, which is not the same as unread: after a jump to a date the rows
	// between here and the tail are items the reader read last week, and the pill used to offer them
	// back as "93 new messages" (D-10 D6). Past that ceiling the pill says the one thing that is
	// true — the way back down.
	const unseen = useMemo(() => {
		let count = 0
		let first: R | undefined
		for (let index = 0; index < shapes.length; index++) {
			const shape = shapes[index]!
			if (shape.seq === 0 || shape.seq <= seenSeq || shape.own === true) continue
			count++
			first ??= rows[index]
		}
		return { count, first }
	}, [rows, shapes, seenSeq])
	const unseenCount = unseen.count + newerCount
	const counted = unseenCount > 0 && unseenCount <= unread
	const pillLabel =
		counted && newLabel !== undefined
			? newLabel({ count: unseenCount, first: unseen.first })
			: t("list.jumpToLatest")

	return (
		// The scroller carries `log`, and the <ol> keeps its list role: a reader is told how many
		// items there are and where in them they stand, which an <ol> whose role was overridden could
		// not say (WCAG 1.3.1). `aria-live="off"` because an arriving item never moves the focus and
		// never interrupts what is being read: the surface announces the item itself through the
		// shell's one announcer (chat's ConversationView), which holds a burst of arrivals to one
		// utterance every three seconds.
		<div
			className={`tb-list${atBottom ? "" : " tb-list--pill"}`}
			ref={scrollerRef}
			onScroll={onScroll}
			role="log"
			aria-live="off"
		>
			<div className="tb-list__sentinel" ref={sentinelRef} aria-hidden="true" />
			{showEmpty ? <div className="tb-list__empty">{empty}</div> : null}
			<ol className="tb-list__rows" ref={listRef} hidden={showEmpty}>
				{loadingOlder ? <li className="tb-row tb-row--loading">{loadingOlderLabel}</li> : null}
				<ActiveRowContext value={activeRows}>
					{rows.slice(start, end).map((row, offset) => {
						const shape = shapes[start + offset]!
						const seq = shape.seq
						return (
							<li
								key={shape.key}
								className={rowClass(shape, highlightSeq !== 0 && seq === highlightSeq)}
								{...(seq > 0
									? { "data-seq": seq, tabIndex: seq === activeSeq ? 0 : -1 }
									: shape.goneSeq !== undefined
										? { "data-gone": shape.goneSeq }
										: {})}
								onFocus={seq > 0 ? () => setFocusedSeq(seq) : undefined}
							>
								{renderRow(row)}
							</li>
						)
					})}
				</ActiveRowContext>
			</ol>
			{atBottom ? null : (
				// It floats over the rows, so its ground, its edge and its shadow are `.tb-elevated`'s
				// (ui/ui.css) and `list.css` writes only where it stands and how it arrives.
				<button
					type="button"
					className="tb-list__jump tb-elevated"
					onClick={() => {
						onJumpToLatest?.()
						scrollToBottom()
					}}
				>
					<Icon name="chevronDown" size={16} />
					{pillLabel}
				</button>
			)}
		</div>
	)
}
